import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaFileAlt, FaTrash, FaUpload } from "react-icons/fa";
import { resolveImageUrl } from "../config/apiConfig";
import { useResizableColumns } from "../hooks/useResizableColumns";
import {
  deleteSubTaskFile,
  extractSubTaskFilesFromPayload,
  fetchSubTaskFiles,
  filterFilesForSubTask,
  uploadSubTaskFile
} from "./taskFileService";
import "../assets/css/Issues.css";
import "../assets/css/ViewCustomer.css";

function fixFileUrl(url) {
  if (!url || typeof url !== "string") return url;
  return resolveImageUrl(url);
}

function formatUploadedOn(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return String(value);
  }
}

/**
 * GET /files?sub_task_id= + POST /file with sub_task_id
 */
export default function SubTaskFileAttachments({
  subTask,
  subTaskId,
  canUpload = false,
  canDelete,
  onRefresh,
  title = "Subtask attachments"
}) {
  const allowDelete = canDelete !== undefined ? canDelete : canUpload;

  const resolvedSubTaskId = useMemo(() => {
    const raw = subTask?.Sub_Task_Id ?? subTask?.sub_task_id ?? subTaskId;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [subTask?.Sub_Task_Id, subTask?.sub_task_id, subTaskId]);

  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [apiFiles, setApiFiles] = useState([]);
  const inputRef = useRef(null);
  const ATTACHMENT_COL_DEFAULT_WIDTHS = allowDelete ? [340, 190, 170, 100] : [420, 220, 190];
  const { getColumnStyle, handleResizeStart } = useResizableColumns(ATTACHMENT_COL_DEFAULT_WIDTHS);

  const fromPayload = useMemo(() => extractSubTaskFilesFromPayload(subTask), [subTask]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (resolvedSubTaskId == null) {
        setApiFiles([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setMessage({ type: "", text: "" });
      try {
        const remote = await fetchSubTaskFiles(resolvedSubTaskId);
        if (!cancelled) setApiFiles(Array.isArray(remote) ? remote : []);
      } catch (err) {
        console.error("Subtask attachments load:", err);
        if (!cancelled) {
          setApiFiles([]);
          setMessage({
            type: "err",
            text: err.response?.data?.message || err.response?.data?.error || err.message || "Could not load attachments."
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [resolvedSubTaskId]);

  const displayFiles = useMemo(() => {
    if (resolvedSubTaskId == null) return [];
    const merged = [...apiFiles, ...fromPayload];
    const scoped = filterFilesForSubTask(merged, resolvedSubTaskId);
    const map = new Map();
    scoped.forEach((f) => {
      const key = f.fileId ?? f.fileUrl ?? f.fileName;
      if (key != null) map.set(String(key), f);
    });
    return [...map.values()];
  }, [fromPayload, apiFiles, resolvedSubTaskId]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || resolvedSubTaskId == null) return;
    setUploading(true);
    setMessage({ type: "", text: "" });
    try {
      await uploadSubTaskFile(resolvedSubTaskId, file);
      setMessage({ type: "ok", text: "File uploaded successfully." });
      if (onRefresh) await onRefresh();
      const remote = await fetchSubTaskFiles(resolvedSubTaskId);
      setApiFiles(Array.isArray(remote) ? remote : []);
    } catch (err) {
      console.error("Subtask file upload:", err);
      setMessage({
        type: "err",
        text: err.response?.data?.message || err.response?.data?.error || err.message || "Upload failed."
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (fileId == null || !Number.isFinite(Number(fileId))) return;
    if (!window.confirm("Delete this attachment? This cannot be undone.")) return;
    setDeletingId(Number(fileId));
    setMessage({ type: "", text: "" });
    try {
      await deleteSubTaskFile(fileId);
      setMessage({ type: "ok", text: "Attachment deleted." });
      if (onRefresh) await onRefresh();
      const remote = await fetchSubTaskFiles(resolvedSubTaskId);
      setApiFiles(Array.isArray(remote) ? remote : []);
    } catch (err) {
      console.error("Subtask file delete:", err);
      setMessage({
        type: "err",
        text: err.response?.data?.message || err.response?.data?.error || err.message || "Delete failed."
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (resolvedSubTaskId == null) {
    return (
      <div className="viewlead-section issue-file-attachments-inner">
        <p className="viewlead-no-data">Unable to determine subtask id for attachments.</p>
      </div>
    );
  }

  return (
    <div className="viewlead-section issue-file-attachments-inner">
      <div className="viewlead-section-header">
        <h3>{title}</h3>
        <div className="viewlead-section-actions">
          {canUpload ? (
            <>
              <input ref={inputRef} type="file" className="issue-files-input-hidden" onChange={handleFileChange} />
              <button
                type="button"
                className="btn-add-attachment"
                disabled={uploading || loading}
                onClick={() => inputRef.current?.click()}
                title="Upload file"
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <FaUpload size={14} />
                {uploading ? "Uploading…" : "Upload file"}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {message.text ? (
        <div
          className={message.type === "err" ? "issues-alert issues-alert-error" : "issues-alert"}
          style={{ marginBottom: 12 }}
        >
          {message.text}
        </div>
      ) : null}

      {loading ? (
        <p className="viewlead-no-data" style={{ margin: 0 }}>
          Loading attachments…
        </p>
      ) : displayFiles.length > 0 ? (
        <table className="viewlead-table">
          <thead>
            <tr>
              <th style={getColumnStyle(0)}>
                File name
                <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(0, e); }} />
              </th>
              <th style={getColumnStyle(1)}>
                Uploaded at
                <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(1, e); }} />
              </th>
              <th style={getColumnStyle(2)}>
                Open
                <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(2, e); }} />
              </th>
              {allowDelete ? <th className="issue-attachments-actions-col" style={getColumnStyle(3)}>Action</th> : null}
            </tr>
          </thead>
          <tbody>
            {displayFiles.map((f) => {
              const href = fixFileUrl(f.fileUrl);
              return (
                <tr key={String(f.fileId ?? f.fileUrl ?? f.fileName)}>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <FaFileAlt style={{ color: "#3182ce", flexShrink: 0 }} aria-hidden />
                      <span>{f.fileName || "—"}</span>
                    </span>
                  </td>
                  <td className="muted-cell">{formatUploadedOn(f.uploadedOn)}</td>
                  <td>
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="viewlead-file-link">
                        View
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  {allowDelete ? (
                    <td className="issue-attachments-actions-cell">
                      {f.fileId != null ? (
                        <button
                          type="button"
                          className="btn-issue-file-delete"
                          disabled={uploading || loading || deletingId === Number(f.fileId)}
                          onClick={() => handleDelete(f.fileId)}
                          title="Delete attachment"
                          aria-label={`Delete ${f.fileName || "file"}`}
                        >
                          {deletingId === Number(f.fileId) ? "…" : <FaTrash size={14} />}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="viewlead-no-data">No attachments yet. {canUpload ? 'Click "Upload file" to add one.' : null}</p>
      )}
    </div>
  );
}

